import { useEffect, useState } from 'react';
import { AlertCircle, Webhook as WebhookIcon } from 'lucide-react';
import {
  WEBHOOK_EVENT_CODES,
  type WebhookEventCode,
  type WebhookListItem,
  createWebhookSchema,
} from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateWebhook, useUpdateWebhook, webhookErrorMessage } from '@/lib/webhooks';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: WebhookListItem;
  onCreated?: (secret: string) => void;
}

const EVENT_LABELS: Record<WebhookEventCode, string> = {
  'post.published': 'บทความเผยแพร่',
  'post.unpublished': 'บทความถอนเผยแพร่',
  'post.deleted': 'บทความถูกลบ',
  'post.updated': 'บทความแก้ไข',
  'category.updated': 'หมวดหมู่เปลี่ยน (สร้าง/แก้/ลบ/จัดลำดับ)',
};

interface FormState {
  url: string;
  events: Set<WebhookEventCode>;
  description: string;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  url: '',
  events: new Set(['post.published']),
  description: '',
  isActive: true,
});

export function WebhookEditDialog({ open, onOpenChange, editing, onCreated }: Props) {
  const isEdit = Boolean(editing);
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFieldError(null);
    create.reset();
    update.reset();
    if (editing) {
      setForm({
        url: editing.url,
        events: new Set(editing.events),
        description: editing.description ?? '',
        isActive: editing.isActive,
      });
    } else {
      setForm(emptyForm());
    }
    // mutation identities change each render — must NOT be in deps (loop trap)
  }, [open, editing]);

  const toggleEvent = (event: WebhookEventCode) => {
    setForm((f) => {
      const next = new Set(f.events);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return { ...f, events: next };
    });
  };

  const handleSubmit = () => {
    setFieldError(null);
    const parsed = createWebhookSchema.safeParse({
      url: form.url,
      events: [...form.events],
      description: form.description || undefined,
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง');
      return;
    }
    if (isEdit && editing) {
      update.mutate(
        {
          id: editing.id,
          input: {
            url: parsed.data.url,
            events: parsed.data.events,
            description: parsed.data.description ?? null,
            isActive: form.isActive,
          },
        },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create.mutate(parsed.data, {
        onSuccess: (webhook) => {
          if (webhook.secret && onCreated) onCreated(webhook.secret);
          onOpenChange(false);
        },
      });
    }
  };

  const mutationError = create.error ?? update.error;
  const isPending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WebhookIcon className="text-brand-600 h-5 w-5" />
            {isEdit ? 'แก้ไข Webhook' : 'สร้าง Webhook ใหม่'}
          </DialogTitle>
          <DialogDescription>
            ระบบจะ POST payload ที่ลงนามด้วย HMAC-SHA256 ไปยัง URL นี้เมื่อ event ที่เลือกเกิดขึ้น
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(fieldError || Boolean(mutationError)) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {fieldError ?? webhookErrorMessage(mutationError)}
              </AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="webhook-url" className="mb-1.5 block">
              URL ปลายทาง
            </Label>
            <Input
              id="webhook-url"
              type="url"
              placeholder="https://example.com/webhook"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
            />
            <p className="mt-1 text-xs text-slate-500">
              ต้องเป็น https:// (อนุญาต http://localhost สำหรับทดสอบ dev)
            </p>
          </div>

          <div>
            <Label className="mb-1.5 block">Events ที่จะรับ</Label>
            <div className="space-y-1.5">
              {WEBHOOK_EVENT_CODES.map((event) => (
                <label
                  key={event}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <input
                    type="checkbox"
                    checked={form.events.has(event)}
                    onChange={() => toggleEvent(event)}
                    className="h-4 w-4"
                  />
                  <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                    {event}
                  </span>
                  <span className="text-slate-500">— {EVENT_LABELS[event]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="webhook-desc" className="mb-1.5 block">
              คำอธิบาย (ไม่บังคับ)
            </Label>
            <Input
              id="webhook-desc"
              placeholder="เช่น Slack #content-team / Mirror to staging"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>

          {isEdit && (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="h-4 w-4"
              />
              <span>เปิดใช้งาน</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || form.events.size === 0}>
            {isPending ? 'กำลังบันทึก...' : isEdit ? 'บันทึก' : 'สร้าง'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
